import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

import { GlobalActions } from '@/store'
import { getCanvasState } from '../helpers/canvas'

type Payload = {
  ref: HTMLElement | null
  translate: number[]
  velocity: number[]
  dxy: number[]
}

const useMomentum = () => {
  const dispatch = useDispatch()
  const momentumId = useRef<number | null>(null)

  const begin = (payload: Payload) => {
    cancel()
    const dxyLength = Math.sqrt(payload.dxy[0] ** 2 + payload.dxy[1] ** 2)
    if (dxyLength === 0) {
      return
    }
    const dxyNormalized = [
      payload.dxy[0] / dxyLength,
      payload.dxy[1] / dxyLength,
    ]
    momentumId.current = requestAnimationFrame((t) =>
      update({ ...payload, dxy: dxyNormalized, msPrev: t, msCur: t })
    )
  }

  const cancel = () => {
    if (momentumId.current !== null) {
      cancelAnimationFrame(momentumId.current)
    }
    momentumId.current = null
  }

  useEffect(() => {
    window.addEventListener('wheel', cancel, { passive: true })
    return () => {
      window.removeEventListener('wheel', cancel)
      cancel()
    }
  }, [])

  const update = (payload: Payload & { msPrev: number; msCur: number }) => {
    const velocity = [...payload.velocity]

    if (payload.ref && momentumId.current !== null) {
      const { translate } = getCanvasState(payload.ref)

      const tx = translate[0]
      const ty = translate[1]

      const velocityX = velocity[0] > 1 ? 1 : velocity[0]
      const velocityY = velocity[1] > 1 ? 1 : velocity[1]

      const dt = payload.msCur - payload.msPrev

      const x = tx + payload.dxy[0] * velocityX * dt
      const y = ty + payload.dxy[1] * velocityY * dt

      dispatch(
        GlobalActions.updateCanvas({
          translate: [x, y],
        })
      )

      velocity[0] *= 0.88
      velocity[1] *= 0.88

      if (Math.abs(velocity[0]) > 0.04 || Math.abs(velocity[1]) > 0.04) {
        momentumId.current = requestAnimationFrame((t) => {
          update({ ...payload, velocity, msPrev: payload.msCur, msCur: t })
        })
      } else {
        cancel()
      }
    }
  }

  return {
    begin,
    cancel,
  }
}

export default useMomentum
